import React, { useState, useEffect, useRef, memo, useMemo } from "react";
import { toast } from "sonner";
import { UtensilsCrossed, Upload, X, Calendar, FileText, Image, Loader2, ChevronLeft, ChevronRight, Lightbulb, CheckCircle2, ZoomIn, ZoomOut, RefreshCw, Trash2, FileSpreadsheet, Download, Printer, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import AppShell from "@/components/layout/AppShell";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import FeatureGate from "@/components/FeatureGate";

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.mjs`;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

// PDF Viewer Component - defined outside to prevent re-creation on each render
const PDFViewerInline = memo(({ fileUrl, filename }) => {
  const [numPages, setNumPages] = useState(null);
  // Load saved zoom from localStorage or default to 100%
  const [scale, setScale] = useState(() => {
    const saved = localStorage.getItem('dailyMenuZoom');
    return saved ? parseFloat(saved) : 1.0;
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [zoomInput, setZoomInput] = useState(Math.round(scale * 100).toString());

  const actualUrl = useMemo(() => `${fileUrl}&r=${refreshKey}`, [fileUrl, refreshKey]);

  // Update zoom input when scale changes
  useEffect(() => {
    setZoomInput(Math.round(scale * 100).toString());
  }, [scale]);

  const handleZoomChange = (newScale) => {
    const clamped = Math.max(0.25, Math.min(2.0, newScale));
    setScale(clamped);
  };

  const handleZoomInputBlur = () => {
    const value = parseInt(zoomInput, 10);
    if (!isNaN(value) && value >= 25 && value <= 200) {
      handleZoomChange(value / 100);
    } else {
      setZoomInput(Math.round(scale * 100).toString());
    }
  };

  const handleZoomInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleZoomInputBlur();
    }
  };

  const handleSaveZoom = () => {
    localStorage.setItem('dailyMenuZoom', scale.toString());
    toast.success(`Zoom saved at ${Math.round(scale * 100)}%`);
  };

  const handleRefresh = () => {
    setLoading(true);
    setError(false);
    setRefreshKey(k => k + 1);
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setLoading(false);
  };

  const onDocumentLoadError = () => {
    setError(true);
    setLoading(false);
  };

  if (error) {
    return (
      <div className="w-full rounded-xl overflow-hidden shadow-lg bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200">
        <div className="flex flex-col items-center justify-center py-16 px-8">
          <div className="w-20 h-20 bg-red-100 rounded-2xl flex items-center justify-center mb-6">
            <FileText className="w-10 h-10 text-red-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">{filename}</h3>
          <p className="text-gray-500 mb-6">Unable to preview - Click to view</p>
          <Button onClick={() => window.open(fileUrl, '_blank')} className="bg-blue-600 hover:bg-blue-700">
            <FileText className="w-4 h-4 mr-2" />
            Open PDF
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full rounded-xl overflow-hidden shadow-lg border border-gray-200 bg-gray-100">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 bg-white border-b">
        <span className="text-sm text-gray-600 font-medium">{filename}</span>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => handleZoomChange(scale - 0.1)} title="Zoom out 10%">
            <ZoomOut className="w-4 h-4" />
          </Button>
          <input
            type="text"
            value={zoomInput}
            onChange={(e) => setZoomInput(e.target.value)}
            onBlur={handleZoomInputBlur}
            onKeyDown={handleZoomInputKeyDown}
            className="w-14 h-8 text-sm text-center border rounded px-1"
            title="Enter zoom % (25-200)"
          />
          <span className="text-sm text-gray-400">%</span>
          <Button variant="outline" size="sm" onClick={() => handleZoomChange(scale + 0.1)} title="Zoom in 10%">
            <ZoomIn className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={handleSaveZoom} title="Save zoom preference">
            Save
          </Button>
          <Button variant="outline" size="sm" onClick={handleRefresh} title="Refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button variant="outline" size="sm" onClick={() => window.open(fileUrl, '_blank')}>
            Open
          </Button>
        </div>
      </div>
      
      {/* PDF Content */}
      <div className="overflow-auto bg-gray-200 p-4" style={{ maxHeight: '700px' }}>
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <span className="ml-2 text-gray-600">Loading PDF...</span>
          </div>
        )}
        <Document
          file={actualUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading=""
        >
          <div className="flex flex-col items-center gap-4">
            {Array.from(new Array(numPages), (el, index) => (
              <Page
                key={`page_${index + 1}`}
                pageNumber={index + 1}
                scale={scale}
                className="shadow-lg"
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            ))}
          </div>
        </Document>
      </div>
      
      {numPages && (
        <div className="px-4 py-2 bg-white border-t text-center text-sm text-gray-500">
          {numPages} page{numPages > 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
});

// Excel Table Viewer - parses and displays Excel menus as a table
const ExcelTableView = ({ menu, fileUrl, token, menuTypeParam }) => {
  const [rows, setRows] = useState([]);
  const [merges, setMerges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedCells, setEditedCells] = useState({}); // {r_c: value}
  const [saving, setSaving] = useState(false);
  const tableRef = React.useRef(null);

  useEffect(() => {
    const fetchExcelData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${API}/daily-menu/${menu.month}/excel-data?token=${encodeURIComponent(token)}&menu_type=${menuTypeParam}`
        );
        setRows(response.data.rows || []);
        setMerges(response.data.merges || []);
      } catch (err) {
        setError("Could not parse Excel file");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchExcelData();
  }, [menu.month, token, menuTypeParam]);

  // Build a set of cells to skip (merged away) and a map of merge origins
  const mergeMap = {};
  const skipSet = new Set();
  merges.forEach(m => {
    mergeMap[`${m.minRow - 1},${m.minCol - 1}`] = {
      rowSpan: m.maxRow - m.minRow + 1,
      colSpan: m.maxCol - m.minCol + 1
    };
    for (let r = m.minRow - 1; r <= m.maxRow - 1; r++) {
      for (let c = m.minCol - 1; c <= m.maxCol - 1; c++) {
        if (r !== m.minRow - 1 || c !== m.minCol - 1) {
          skipSet.add(`${r},${c}`);
        }
      }
    }
  });

  const handlePrint = () => {
    if (!tableRef.current) return;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html><head><title>${menu.filename}</title>
      <style>body{font-family:Arial,sans-serif;padding:20px}table{border-collapse:collapse;width:100%}
      td,th{border:1px solid #ccc;padding:6px 10px;font-size:13px}
      @media print{body{padding:0}}</style></head>
      <body><h2>${menu.filename}</h2>
      ${tableRef.current.outerHTML}</body></html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const handleSaveEdits = async () => {
    setSaving(true);
    try {
      // Build edited_rows: sparse array matching original rows, only changed cells
      const edited = rows.map((row, r) =>
        row.map((cell, c) => {
          const key = `${r}_${c}`;
          return key in editedCells ? editedCells[key] : cell.v;
        })
      );
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      await axios.patch(
        `${API}/daily-menu/${menu.month}/excel-data?menu_type=${menuTypeParam}`,
        { edited_rows: edited },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Reload to get merged state
      const res = await axios.get(
        `${API}/daily-menu/${menu.month}/excel-data?token=${encodeURIComponent(token)}&menu_type=${menuTypeParam}`
      );
      setRows(res.data.rows || []);
      setMerges(res.data.merges || []);
      setEditedCells({});
      setEditMode(false);
      toast.success("Menu saved!");
    } catch { toast.error("Failed to save edits"); }
    finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
        <span className="ml-3 text-gray-500">Loading menu data...</span>
      </div>
    );
  }

  if (error || rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-4">
        <div className="p-4 bg-green-100 rounded-full">
          <FileSpreadsheet className="w-12 h-12 text-green-700" />
        </div>
        <p className="text-gray-500">{error === "Could not parse Excel file" ? "Menu file not found — please re-upload" : (error || "No data found in Excel file")}</p>
        <a href={fileUrl} download={menu.filename} className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
          <Download className="w-4 h-4" />
          Download Excel File
        </a>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        {/* Edit Mode Toggle — always visible for Excel */}
        <button
          onClick={() => { setEditMode(e => !e); setEditedCells({}); }}
          className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-semibold rounded-lg border-2 transition-all ${
            editMode
              ? 'bg-blue-600 border-blue-600 text-white shadow-md'
              : 'bg-white border-blue-400 text-blue-600 hover:bg-blue-50'
          }`}
          data-testid="edit-menu-toggle"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
          {editMode ? 'Editing — Click Save to Commit' : 'Edit Menu'}
        </button>
        <div className="flex gap-2 ml-auto">
          {editMode && (
            <>
              <button
                onClick={handleSaveEdits}
                disabled={saving}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold"
                data-testid="save-menu-edits"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                onClick={() => { setEditMode(false); setEditedCells({}); }}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
            </>
          )}
          {!editMode && (
            <>
              <button onClick={handlePrint} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200" data-testid="print-excel-menu">
                <Printer className="w-4 h-4" />Print
              </button>
              <a href={fileUrl} download={menu.filename} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700" data-testid="download-excel-menu">
                <Download className="w-4 h-4" />Download
              </a>
            </>
          )}
        </div>
      </div>
      <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
        <table ref={tableRef} className="w-full text-sm border-collapse" data-testid="excel-menu-table">
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => {
                  if (skipSet.has(`${i},${j}`)) return null;
                  const mg = mergeMap[`${i},${j}`];
                  const s = cell.s || {};
                  const cellStyle = {};
                  if (s.bg) cellStyle.backgroundColor = s.bg;
                  if (s.color) cellStyle.color = s.color;
                  if (s.bold) cellStyle.fontWeight = 'bold';
                  if (s.fontSize) cellStyle.fontSize = `${s.fontSize}px`;
                  if (s.align) cellStyle.textAlign = s.align;
                  const cellKey = `${i}_${j}`;
                  const displayVal = cellKey in editedCells ? editedCells[cellKey] : (cell.v || '');
                  return (
                    <td
                      key={j}
                      style={cellStyle}
                      className={`px-3 py-2 border border-gray-200 whitespace-pre-wrap ${editMode ? 'bg-blue-50/30' : ''}`}
                      {...(mg ? { rowSpan: mg.rowSpan, colSpan: mg.colSpan } : {})}
                    >
                      {editMode ? (
                        <input
                          type="text"
                          value={displayVal}
                          onChange={e => setEditedCells(prev => ({ ...prev, [cellKey]: e.target.value }))}
                          className="w-full bg-white border border-blue-300 rounded px-1 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-[60px]"
                          style={{ fontWeight: s.bold ? 'bold' : 'normal', color: s.color || 'inherit' }}
                        />
                      ) : displayVal}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Hero carousel images for Daily Menu
const HERO_IMAGES = [
  {
    url: "https://static.prod-images.emergentagent.com/jobs/8e8a83cd-e63a-403d-8aa9-2fd4560e24e8/images/d52fe9e3a2d1adb6dcd509da43c55a01c996fbd98bc24d2390e4d5e2ed51462f.png",
    alt: "Teacher with toddlers at lunch"
  },
  {
    url: "https://static.prod-images.emergentagent.com/jobs/8e8a83cd-e63a-403d-8aa9-2fd4560e24e8/images/9649eeaba4464c51c3c361091cc9c9c63d23016e1fc4448c1072489567c6ea7f.png",
    alt: "Diverse toddlers eating at daycare table"
  },
  {
    url: "https://static.prod-images.emergentagent.com/jobs/8e8a83cd-e63a-403d-8aa9-2fd4560e24e8/images/5a7b328e0f6758682e1751c698d8bbbc0f9e9dcd57001afb8edbd241d22321ec.png",
    alt: "Black female teacher serving toddlers"
  },
  {
    url: "https://static.prod-images.emergentagent.com/jobs/8e8a83cd-e63a-403d-8aa9-2fd4560e24e8/images/7500419bb6ece6fd27eb31058fed52ae7c7bfe2b494a035e3df0e01f06620162.png",
    alt: "Colorful healthy lunch plates"
  }
];

const DailyMenuPage = ({ menuType = "lunch" }) => {
  const { user, token } = useAuth();
  const [allMenus, setAllMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [viewingMonth, setViewingMonth] = useState(new Date().getMonth() + 1);
  const [viewingMenu, setViewingMenu] = useState(null);
  const [heroIndex, setHeroIndex] = useState(0);
  const fileInputRef = useRef(null);
  const [confirmDeleteMonth, setConfirmDeleteMonth] = useState(null);
  const [overwriteWarning, setOverwriteWarning] = useState(null);
  const [copying, setCopying] = useState(false);

  const menuTypeParam = menuType === "breakfast_snack" ? "breakfast_snack" : "lunch";
  const menuLabel = menuType === "breakfast_snack" ? "Breakfast/Snack" : "Lunch";

  const currentMonth = new Date().getMonth() + 1;
  const currentMonthName = MONTHS[currentMonth - 1];
  
  // Auth headers for API calls
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  // Auto-advance hero carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setHeroIndex((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (token) {
      fetchAllMenus();
    }
  }, [token, menuTypeParam]);

  // When viewingMonth changes, find that month's menu from allMenus
  useEffect(() => {
    if (allMenus.length > 0) {
      const menuForMonth = allMenus.find(m => m.month === viewingMonth);
      setViewingMenu(menuForMonth || null);
    } else if (!loading) {
      setViewingMenu(null);
    }
  }, [viewingMonth, allMenus, loading]);

  // Restore viewingMonth from localStorage on mount so navigating away and back
  // keeps the user on the same month they were editing.
  useEffect(() => {
    const stored = parseInt(localStorage.getItem(`menuViewingMonth_${menuTypeParam}`));
    if (stored >= 1 && stored <= 12) setViewingMonth(stored);
  }, []); // eslint-disable-line

  // Persist viewingMonth to localStorage whenever it changes.
  useEffect(() => {
    localStorage.setItem(`menuViewingMonth_${menuTypeParam}`, viewingMonth);
  }, [viewingMonth, menuTypeParam]);

  const navigateMonth = (direction) => {
    setViewingMonth(prev => {
      const next = prev + direction;
      if (next < 1) return 12;
      if (next > 12) return 1;
      return next;
    });
  };

  const handleCopyMenuToMonth = async (sourceMonth, targetMonth, overwrite = false) => {
    setCopying(true);
    try {
      const res = await axios.post(
        `${API}/daily-menu/${sourceMonth}/copy-to-month`,
        { target_month: targetMonth, menu_type: menuTypeParam, overwrite },
        { headers: authHeaders }
      );
      if (res.data?.needsConfirmation) {
        setOverwriteWarning({ sourceMonth, targetMonth, message: res.data.message });
        return;
      }
      toast.success(`Menu copied to ${MONTHS[targetMonth - 1]}`);
      setOverwriteWarning(null);
      fetchAllMenus();
    } catch (err) {
      const status = err?.response?.status;
      const body = err?.response?.data;
      if (status === 409 && body?.needsConfirmation) {
        setOverwriteWarning({
          sourceMonth,
          targetMonth,
          message: body.message,
          existingFilename: body.existingFilename,
        });
      } else {
        toast.error(body?.detail || "Failed to copy menu");
      }
    } finally {
      setCopying(false);
    }
  };


  const fetchAllMenus = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/daily-menu?menu_type=${menuTypeParam}`, { headers: authHeaders });
      setAllMenus(response.data.menus || []);
    } catch (error) {
      console.error("Error fetching all menus:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target?.files?.[0] || event.dataTransfer?.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload a PNG, JPG, PDF, or Excel file");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('month', selectedMonth.toString());
    formData.append('uploaded_by', user?.name || user?.email || 'Unknown');
    formData.append('menu_type', menuTypeParam);

    try {
      await axios.post(`${API}/daily-menu/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', ...authHeaders }
      });
      toast.success(`${menuLabel} menu uploaded for ${MONTHS[selectedMonth - 1]}`);
      fetchAllMenus();
      setUploadModalOpen(false);
    } catch (error) {
      console.error("Error uploading menu:", error);
      toast.error("Failed to upload menu");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const getMenuForMonth = (month) => {
    return allMenus.find(m => m.month === month);
  };

  // Locked Edit toggle shown for non-Excel file types
  const LockedEditToggle = () => (
    <div className="flex items-center gap-2 mb-3">
      <button
        disabled
        className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-semibold rounded-lg border-2 bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed"
        title="Edit mode is only available for Excel uploaded menus"
        data-testid="edit-menu-toggle-locked"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
        Edit Menu
      </button>
      <span className="text-xs text-gray-400 italic">Editable toggle is for Excel uploaded menus only</span>
    </div>
  );

  const renderMenuContent = (menu) => {
    if (!menu) return null;

    // Generate file URL for the menu being viewed
    const fileUrl = `${API}/daily-menu/${menu.month}/file?token=${encodeURIComponent(token)}&menu_type=${menuTypeParam}`;

    if (menu.file_type === 'pdf') {
      return (
        <>
          <LockedEditToggle />
          <PDFViewerInline fileUrl={fileUrl} filename={menu.filename || `${MONTHS[menu.month - 1]} Menu.pdf`} />
        </>
      );
    }

    // For Excel files, render as HTML table with Edit/Print/Download buttons
    if (menu.file_type === 'xls' || menu.file_type === 'xlsx') {
      return <ExcelTableView menu={menu} fileUrl={fileUrl} token={token} menuTypeParam={menuTypeParam} />;
    }

    // For images, display directly
    return (
      <>
        <LockedEditToggle />
        <div className="flex justify-center">
          <img
            src={fileUrl}
            alt={`Menu for ${MONTHS[menu.month - 1]}`}
            className="max-w-full h-auto rounded-xl shadow-lg"
            style={{ maxHeight: '600px' }}
          />
        </div>
      </>
    );
  };

  return (
    <AppShell pageTitle={`Center Menu ${menuLabel}`}>
      <FeatureGate feature="menu_planner">
      <div data-testid="daily-menu-page" className="space-y-6">
        {/* Hero Banner Carousel */}
        <div className="relative rounded-2xl overflow-hidden h-48 sm:h-56 md:h-64 lg:h-72 -mx-4 lg:-mx-6 -mt-4 lg:-mt-6 mb-6 bg-blue-100">
          {/* Carousel Images */}
          {HERO_IMAGES.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                index === heroIndex ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={image.url}
                alt={image.alt}
                className="w-full h-full object-cover object-center"
              />
            </div>
          ))}
          <div className="absolute inset-0 bg-gradient-to-r from-[#2F6DB5]/80 via-[#2F6DB5]/60 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white to-transparent" />
          
          <div className="absolute inset-0 flex items-center px-6 sm:px-10 md:px-16">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <UtensilsCrossed className="w-8 h-8 text-white" />
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white drop-shadow-lg">
                  {menuLabel} Menu
                </h1>
              </div>
              <p className="text-white/90 text-lg drop-shadow-md mb-3">
                {currentMonthName} {new Date().getFullYear()}
              </p>
              
              {/* Upload Button - Under month name */}
              <Dialog open={uploadModalOpen} onOpenChange={setUploadModalOpen}>
                <DialogTrigger asChild>
                  <Button
                    className="bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white border border-white/30 rounded-xl px-5 py-2 flex items-center gap-2 shadow-lg"
                    data-testid="upload-menu-btn"
                  >
                    <Upload className="w-4 h-4" />
                    Upload Menu
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[600px]">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-xl">
                    <Calendar className="w-5 h-5 text-blue-600" />
                    Upload Monthly {menuLabel} Menu
                  </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-6 py-4">
                  {/* How It Works - Inside modal */}
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <h4 className="font-semibold text-blue-800 flex items-center gap-2 mb-2">
                      <Calendar className="w-4 h-4" />
                      How It Works
                    </h4>
                    <ul className="space-y-1.5 text-sm text-blue-700">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                        Upload menus for any month using the grid below
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                        The current month's menu displays automatically
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                        On the 1st of each month, the menu switches automatically
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                        Green checkmarks (✓) show which months have menus uploaded
                      </li>
                    </ul>
                  </div>

                  {/* Month Selector */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Select Month
                    </label>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {MONTHS.map((month, index) => {
                        const monthNum = index + 1;
                        const hasMenu = getMenuForMonth(monthNum);
                        return (
                          <button
                            key={month}
                            onClick={() => setSelectedMonth(monthNum)}
                            className={`p-3 rounded-xl text-sm font-medium transition-all ${
                              selectedMonth === monthNum
                                ? 'bg-blue-600 text-white shadow-md'
                                : hasMenu
                                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                            data-testid={`month-btn-${monthNum}`}
                          >
                            {month.substring(0, 3)}
                            {hasMenu && selectedMonth !== monthNum && (
                              <span className="ml-1 text-xs">✓</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* File Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Upload Menu for {MONTHS[selectedMonth - 1]}
                    </label>
                    <div
                      className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
                        dragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400'
                      }`}
                      onClick={() => fileInputRef.current?.click()}
                      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragging(true); }}
                      onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDragging(true); }}
                      onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragging(false); }}
                      onDrop={(e) => { e.preventDefault(); e.stopPropagation(); setDragging(false); handleFileUpload(e); }}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".png,.jpg,.jpeg,.pdf,.xls,.xlsx"
                        onChange={handleFileUpload}
                        className="hidden"
                        data-testid="file-input"
                      />
                      {uploading ? (
                        <div className="flex flex-col items-center gap-3">
                          <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
                          <p className="text-gray-600">Uploading...</p>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-3">
                          <div className="flex gap-2">
                            <Image className="w-8 h-8 text-gray-400" />
                            <FileText className="w-8 h-8 text-gray-400" />
                          </div>
                          <p className="text-gray-600">
                            Click to upload or drag and drop
                          </p>
                          <p className="text-sm text-gray-400">
                            PNG, JPG, PDF, or Excel (max 10MB)
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Current menu for selected month */}
                  {getMenuForMonth(selectedMonth) && (
                    <div className="bg-blue-50 rounded-xl p-4">
                      <p className="text-sm text-blue-700">
                        <strong>{MONTHS[selectedMonth - 1]}</strong> already has a menu uploaded.
                        Uploading a new file will replace it.
                      </p>
                      <p className="text-xs text-blue-500 mt-1">
                        Last updated: {new Date(getMenuForMonth(selectedMonth).uploaded_at).toLocaleDateString()}
                        {' by '}{getMenuForMonth(selectedMonth).uploaded_by}
                      </p>
                    </div>
                  )}
                  
                  {/* Cancel Button */}
                  <div className="flex justify-end pt-2">
                    <DialogTrigger asChild>
                      <Button variant="outline">
                        Cancel
                      </Button>
                    </DialogTrigger>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            </div>
          </div>
          
          {/* Carousel indicator dots */}
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
            {HERO_IMAGES.map((_, index) => (
              <button
                key={index}
                onClick={() => setHeroIndex(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === heroIndex 
                    ? 'bg-white w-4' 
                    : 'bg-white/50 hover:bg-white/70'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Current Month's Menu Display */}
        <Card className="border-0 shadow-xl rounded-2xl overflow-hidden">
          <CardContent className="p-6 sm:p-8">
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-6">
              <Button variant="outline" size="sm" onClick={() => navigateMonth(-1)} data-testid="prev-month-btn">
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <div className="text-center">
                <h2 className="text-2xl font-bold text-gray-800" data-testid="viewing-month-title">
                  {MONTHS[viewingMonth - 1]} Menu
                </h2>
                {viewingMonth === currentMonth && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">Current Month</span>
                )}
              </div>
              <Button variant="outline" size="sm" onClick={() => navigateMonth(1)} data-testid="next-month-btn">
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>

            {/* Month Quick-Jump Pills */}
            <div className="flex flex-wrap gap-1.5 justify-center mb-6">
              {MONTHS.map((month, index) => {
                const monthNum = index + 1;
                const hasMenu = allMenus.some(m => m.month === monthNum);
                const isViewing = viewingMonth === monthNum;
                return (
                  <button
                    key={month}
                    onClick={() => setViewingMonth(monthNum)}
                    data-testid={`view-month-pill-${monthNum}`}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                      isViewing
                        ? 'bg-blue-600 text-white shadow-md'
                        : hasMenu
                        ? 'bg-green-100 text-green-700 hover:bg-green-200 border border-green-300'
                        : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                    }`}
                  >
                    {month.substring(0, 3)}
                    {hasMenu && !isViewing && ' ✓'}
                  </button>
                );
              })}
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
              </div>
            ) : viewingMenu ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">
                      Updated {new Date(viewingMenu.uploaded_at).toLocaleDateString()}
                      {' by '}{viewingMenu.uploaded_by}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      {viewingMenu.file_type === 'pdf' ? (
                        <FileText className="w-5 h-5" />
                      ) : viewingMenu.file_type === 'xls' || viewingMenu.file_type === 'xlsx' ? (
                        <FileSpreadsheet className="w-5 h-5" />
                      ) : (
                        <Image className="w-5 h-5" />
                      )}
                      {viewingMenu.filename}
                    </div>
                    {confirmDeleteMonth === viewingMenu.month ? (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-red-600 font-medium">Delete this menu?</span>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 border-red-400 hover:bg-red-50 h-7 px-2 text-xs"
                          onClick={async () => {
                            try {
                              await axios.delete(`${API}/daily-menu/${viewingMenu.month}?menu_type=${menuTypeParam}`, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              setConfirmDeleteMonth(null);
                              toast.success(`${MONTHS[viewingMonth - 1]} ${menuLabel.toLowerCase()} menu deleted`);
                              fetchAllMenus();
                            } catch (error) {
                              toast.error("Failed to delete: " + (error.response?.data?.detail || error.message));
                            }
                          }}
                        >
                          Yes, Delete
                        </Button>
                        <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={() => setConfirmDeleteMonth(null)}>
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-brand-primary hover:bg-blue-50"
                              data-testid="copy-menu-btn"
                              title="Copy this menu to another month"
                            >
                              <Copy className="w-4 h-4 mr-1" />
                              Copy to month
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-64 p-3" align="end">
                            <div className="text-xs font-semibold text-ink-main mb-2">
                              Copy {menuLabel.toLowerCase()} menu to:
                            </div>
                            <div className="grid grid-cols-3 gap-1.5">
                              {MONTHS.map((mName, mIdx) => {
                                const mNum = mIdx + 1;
                                const isCurrent = mNum === viewingMenu.month;
                                return (
                                  <button
                                    key={mNum}
                                    disabled={isCurrent || copying}
                                    onClick={() => handleCopyMenuToMonth(viewingMenu.month, mNum, false)}
                                    data-testid={`copy-menu-to-month-${mNum}`}
                                    className={`px-2 py-1.5 text-xs rounded-md font-semibold transition ${
                                      isCurrent
                                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                        : "bg-blue-50 text-brand-primary hover:bg-brand-primary hover:text-white"
                                    } disabled:opacity-60`}
                                  >
                                    {mName.substring(0, 3)}
                                  </button>
                                );
                              })}
                            </div>
                            <div className="text-[10px] text-gray-400 mt-2">
                              Current month is greyed out. You'll be asked to confirm if the target already has a menu.
                            </div>
                          </PopoverContent>
                        </Popover>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:bg-red-50 hover:text-red-700"
                          data-testid="delete-menu-btn"
                          onClick={() => setConfirmDeleteMonth(viewingMenu.month)}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                
                {renderMenuContent(viewingMenu)}
              </div>
            ) : (
              <div className="text-center py-20">
                <UtensilsCrossed className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">
                  No Menu Uploaded for {MONTHS[viewingMonth - 1]}
                </h3>
                <p className="text-gray-400 mb-6">
                  Click the "Upload Menu" button to add this month's menu
                </p>
                
                {/* Tip Box */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3 text-left max-w-md mx-auto mb-6">
                  <Lightbulb className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-amber-800 font-medium">Tip: Upload menus in advance!</p>
                    <p className="text-amber-700 text-sm">
                      The system automatically displays each month's menu starting on the 1st. No manual switching needed.
                    </p>
                  </div>
                </div>
                
                <Button
                  onClick={() => {
                    setSelectedMonth(viewingMonth);
                    setUploadModalOpen(true);
                  }}
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
                  data-testid="upload-month-menu-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload {MONTHS[viewingMonth - 1]} Menu
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Copy-overwrite warning dialog */}
      <Dialog
        open={!!overwriteWarning}
        onOpenChange={(o) => { if (!o && !copying) setOverwriteWarning(null); }}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Replace existing menu?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-gray-700">{overwriteWarning?.message}</p>
            {overwriteWarning?.existingFilename && (
              <div className="p-2 bg-gray-50 border rounded text-xs text-gray-700">
                Current file: <strong>{overwriteWarning.existingFilename}</strong>
              </div>
            )}
            <p className="text-xs text-amber-700">
              The replaced menu will be kept for 30 days in case you need to recover it.
            </p>
            <div className="flex gap-2 pt-2 border-t">
              <Button
                variant="outline"
                className="flex-1 rounded-lg"
                onClick={() => setOverwriteWarning(null)}
                disabled={copying}
                data-testid="copy-menu-overwrite-cancel"
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-lg"
                onClick={() => handleCopyMenuToMonth(overwriteWarning.sourceMonth, overwriteWarning.targetMonth, true)}
                disabled={copying}
                data-testid="copy-menu-overwrite-confirm"
              >
                {copying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Copy className="w-4 h-4 mr-2" />}
                Replace
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      </FeatureGate>
    </AppShell>
  );
};

export default DailyMenuPage;
